import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Eye, Tag, RefreshCw, MapPin } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { generateUniqueSlug } from '@/utils/slugify';
import ImageUpload from '@/components/ui/ImageUpload';
const categories = ['電気設備', '機械設備', '太陽光発電', 'その他'];
export default function ProjectEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isEditing = id && id !== 'new';
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generatingSlug, setGeneratingSlug] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        category: '電気設備',
        location: '',
        image_url: '',
        description: '',
        status: 'draft',
    });
    useEffect(() => {
        if (isEditing) {
            fetchProject();
        }
    }, [id, isEditing]);
    const fetchProject = async () => {
        if (!id || id === 'new')
            return;
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('projects')
                .select('*')
                .eq('id', id)
                .single();
            if (error)
                throw error;
            setFormData({
                title: data.title || '',
                slug: data.slug || '',
                category: data.category || '電気設備',
                location: data.location || '',
                image_url: data.image_url || '',
                description: data.description || '',
                status: data.status || 'draft',
            });
        }
        catch (error) {
            console.error('Error fetching project:', error);
            alert('施工実績の取得に失敗しました。');
        }
        finally {
            setLoading(false);
        }
    };
    const handleTitleChange = async (title) => {
        setFormData(prev => ({ ...prev, title }));
        if (!isEditing && title.trim()) {
            setGeneratingSlug(true);
            try {
                const slug = await generateUniqueSlug(title, 'projects');
                setFormData(prev => ({ ...prev, slug }));
            }
            catch (error) {
                console.error('Error generating slug:', error);
            }
            finally {
                setGeneratingSlug(false);
            }
        }
    };
    const handleRegenerateSlug = async () => {
        if (!formData.title.trim()) {
            alert('タイトルを入力してからスラッグを生成してください。');
            return;
        }
        setGeneratingSlug(true);
        try {
            const slug = await generateUniqueSlug(formData.title, 'projects');
            setFormData(prev => ({ ...prev, slug }));
        }
        catch (error) {
            console.error('Error generating slug:', error);
            alert('スラッグの生成に失敗しました。');
        }
        finally {
            setGeneratingSlug(false);
        }
    };
    const handleSubmit = async (e, publishNow = false) => {
        e.preventDefault();
        if (!formData.title || !formData.slug || !formData.location) {
            alert('タイトル、スラッグ、場所は必須項目です。');
            return;
        }
        if (!user) {
            alert('ログインしてください。');
            return;
        }
        setSaving(true);
        try {
            const projectData = {
                ...formData,
                status: publishNow ? 'published' : formData.status,
                updated_at: new Date().toISOString(),
            };
            if (isEditing) {
                const { error } = await supabase
                    .from('projects')
                    .update(projectData)
                    .eq('id', id);
                if (error)
                    throw error;
            }
            else {
                const { error } = await supabase
                    .from('projects')
                    .insert([projectData]);
                if (error)
                    throw error;
            }
            alert(isEditing ? '施工実績を更新しました。' : '施工実績を作成しました。');
            navigate('/admin');
        }
        catch (error) {
            console.error('Error saving project:', error);
            alert('施工実績の保存に失敗しました。');
        }
        finally {
            setSaving(false);
        }
    };
    if (loading) {
        return (<div className='min-h-screen flex items-center justify-center'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-navy'></div>
      </div>);
    }
    return (<div className='min-h-screen bg-gray-50'>
      <header className='bg-white shadow-sm border-b'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center py-4'>
            <div className='flex items-center gap-4'>
              <Link to='/' className='hover:opacity-80 transition-opacity'>
                <h2 className='text-xl font-bebas tracking-wider text-navy'>
                  MORIDEN SETSUBI
                </h2>
              </Link>
              <span className='text-gray-300'>|</span>
              <Link to='/admin' className='inline-flex items-center text-gray-600 hover:text-gray-900'>
                <ArrowLeft className='w-4 h-4 mr-2'/>
                管理画面に戻る
              </Link>
              <h1 className='text-lg font-medium text-gray-900'>
                {isEditing ? '施工実績を編集' : '新しい施工実績を作成'}
              </h1>
            </div>

            <div className='w-32'></div>
          </div>
        </div>
      </header>

      <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} onSubmit={handleSubmit} className='space-y-6'>
          <div className='bg-white shadow-sm rounded-lg p-6 space-y-6'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                タイトル <span className='text-red-500'>*</span>
              </label>
              <input type='text' value={formData.title} onChange={(e) => handleTitleChange(e.target.value)} className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-navy focus:border-navy' placeholder='例: 盛岡市オフィスビル電気設備工事' required/>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                スラッグ <span className='text-red-500'>*</span>
              </label>
              <div className='flex gap-2'>
                <input type='text' value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} className='flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-navy focus:border-navy' placeholder='例: morioka-office-electrical' required/>
                <button type='button' onClick={handleRegenerateSlug} disabled={generatingSlug || !formData.title} className='inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50'>
                  <RefreshCw className={`w-4 h-4 ${generatingSlug ? 'animate-spin' : ''}`}/>
                </button>
              </div>
              <p className='mt-1 text-sm text-gray-500'>
                URLに使用される識別子です。英数字とハイフンのみ使用できます。
              </p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                カテゴリ
              </label>
              <div className='flex items-center gap-2'>
                <Tag className='w-5 h-5 text-gray-400'/>
                <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className='flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-navy focus:border-navy'>
                  {categories.map((cat) => (<option key={cat} value={cat}>
                      {cat}
                    </option>))}
                </select>
              </div>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                場所 <span className='text-red-500'>*</span>
              </label>
              <div className='flex items-center gap-2'>
                <MapPin className='w-5 h-5 text-gray-400'/>
                <input type='text' value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className='flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-navy focus:border-navy' placeholder='例: 岩手県盛岡市' required/>
              </div>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                メイン画像
              </label>
              <ImageUpload value={formData.image_url} onChange={(url) => setFormData({ ...formData, image_url: url })}/>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                説明
              </label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={6} className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-navy focus:border-navy' placeholder='施工実績の詳細な説明を入力してください...'/>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                ステータス
              </label>
              <select value={formData.status} onChange={(e) => setFormData({
            ...formData,
            status: e.target.value,
        })} className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-navy focus:border-navy'>
                <option value='draft'>下書き</option>
                <option value='published'>公開</option>
              </select>
            </div>
          </div>

          <div className='flex justify-end gap-4'>
            <Link to='/admin' className='px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50'>
              キャンセル
            </Link>

            <button type='submit' disabled={saving} className='inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 disabled:opacity-50'>
              <Save className='w-4 h-4 mr-2'/>
              {saving ? '保存中...' : '下書きとして保存'}
            </button>

            <button type='button' onClick={(e) => handleSubmit(e, true)} disabled={saving} className='inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-navy hover:bg-navy/90 disabled:opacity-50'>
              <Eye className='w-4 h-4 mr-2'/>
              {saving ? '保存中...' : '公開'}
            </button>
          </div>
        </motion.form>
      </div>
    </div>);
}
